import React from 'react'
import * as DialogPrimitive from '@grupoboticario/flora-react-dialog'
import {
  baseAnimation,
  FloraVariants,
  forwardRef,
  keyframes,
  styled,
} from '@grupoboticario/flora-react-system'
import { HTMLFloraProps } from '@grupoboticario/flora-react-utils'

export type DrawerProps = DialogPrimitive.DialogProps

/**
 * @component
 * @name Drawer
 * @description Drawer component
 * @tutorial https://flora.gb.tech/docs/components/core/drawer
 */
export const Drawer = (props: DrawerProps) => {
  return <DialogPrimitive.Dialog {...props} data-flora="drawer" />
}

const slideIn = keyframes({
  from: { transform: '$$transformValue' },
  to: { transform: 'translate3d(0,0,0)' },
})

const slideOut = keyframes({
  from: { transform: 'translate3d(0,0,0)' },
  to: { transform: '$$transformValue' },
})

const StyledContent = styled(DialogPrimitive.DialogContent, {
  backgroundColor: '$backgroundPrimary',
  boxShadow: '$colors$shadowLight 0 0 38px -10px, $colors$shadowDark 0 0 35px -15px',
  position: 'fixed',
  top: 0,
  bottom: 0,
  width: '100%',
  willChange: 'transform',
  display: 'flex',
  flexDirection: 'column',
  zIndex: '$modal',

  '&[data-state="open"]': {
    animation: `${slideIn} ${baseAnimation}`,
  },

  '&[data-state="closed"]': {
    animation: `${slideOut} ${baseAnimation}`,
  },

  variants: {
    side: {
      top: {
        $$transformValue: 'translate3d(0,-100%,0)',
        width: '100%',
        height: 'auto',
        minHeight: 60,
        left: 0,
        bottom: 'auto',
      },
      right: {
        $$transformValue: 'translate3d(100%,0,0)',
        right: 0,
        '@tablet': {
          width: 380,
        },
      },
      bottom: {
        $$transformValue: 'translate3d(0,100%,0)',
        width: '100%',
        height: 'auto',
        minHeight: 60,
        bottom: 0,
        left: 0,
        top: 'auto',
      },
      left: {
        $$transformValue: 'translate3d(-100%,0,0)',
        left: 0,
        '@tablet': {
          width: 380,
        },
      },
    },
    isFull: {
      true: {
        width: '100%',
        height: '100%',
      },
    },
  },

  defaultVariants: {
    side: 'right',
  },
})
const StyledBody = styled(DialogPrimitive.DialogBody, {})
const StyledHeader = styled(DialogPrimitive.DialogHeader, {})
const StyledFooter = styled(DialogPrimitive.DialogFooter, {})
const StyledTrigger = styled(DialogPrimitive.DialogTrigger, {})
const StyledClose = styled(DialogPrimitive.DialogClose, {})

export type DrawerContentProps = HTMLFloraProps<'div'> &
  FloraVariants<typeof StyledContent> &
  DialogPrimitive.DialogContentProps

/**
 * @tutorial https://flora.gb.tech/docs/components/core/drawer
 */
export const DrawerContent = forwardRef<'div', DrawerContentProps>((props, forwardedRef) => {
  return <StyledContent {...props} data-flora="drawer-content" ref={forwardedRef} />
})

export type DrawerBodyProps = HTMLFloraProps<'div'> &
  FloraVariants<typeof DialogPrimitive.DialogBody>

/**
 * @tutorial https://flora.gb.tech/docs/components/core/drawer
 */
export const DrawerBody = forwardRef<'div', DrawerBodyProps>((props, forwardedRef) => {
  return <StyledBody {...props} data-flora="drawer-body" ref={forwardedRef} />
})

export type DrawerHeaderProps = HTMLFloraProps<'header'> &
  FloraVariants<typeof DialogPrimitive.DialogHeader>

/**
 * @tutorial https://flora.gb.tech/docs/components/core/drawer
 */
export const DrawerHeader = forwardRef<'header', DrawerHeaderProps>((props, forwardedRef) => {
  return <StyledHeader {...props} data-flora="drawer-header" ref={forwardedRef} />
})

export type DrawerFooterProps = HTMLFloraProps<'footer'> &
  FloraVariants<typeof DialogPrimitive.DialogFooter>

/**
 * @tutorial https://flora.gb.tech/docs/components/core/drawer
 */
export const DrawerFooter = forwardRef<'footer', DrawerFooterProps>((props, forwardedRef) => {
  return <StyledFooter {...props} data-flora="drawer-footer" ref={forwardedRef} />
})

export type DrawerTriggerProps = HTMLFloraProps<'button'> &
  FloraVariants<typeof DialogPrimitive.DialogTrigger> &
  DialogPrimitive.DialogTriggerProps

/**
 * @tutorial https://flora.gb.tech/docs/components/core/drawer
 */
export const DrawerTrigger = forwardRef<'button', DrawerTriggerProps>((props, forwardedRef) => {
  return <StyledTrigger {...props} asChild data-flora="drawer-trigger" ref={forwardedRef} />
})

export type DrawerCloseButtonProps = HTMLFloraProps<'button'> & DialogPrimitive.DialogCloseProps

/**
 * @tutorial https://flora.gb.tech/docs/components/core/drawer
 */
export const DrawerCloseButton = forwardRef<'button', DrawerCloseButtonProps>(
  (props, forwardedRef) => {
    return <StyledClose {...props} data-flora="drawer-close" ref={forwardedRef} />
  },
)
